import ClearAllIcon from '@mui/icons-material/ClearAll';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';

const PropertyCartSidebar = ({
  cartOpen,
  setCartOpen,
  cartItems,
  togglePolygonSelection,
  setShowReport,
  setCartItems,
  setSelectedPolygons,
  onClearAll,
}) => {
  const handleRemoveAll = () => {
    onClearAll(); // Use the passed function
  };

  return (
    <Drawer
      anchor='right'
      open={cartOpen}
      onClose={() => setCartOpen(false)}
      PaperProps={{
        sx: {
          width: 400,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 3,
        },
      }}
    >
      {/* Sidebar Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid #ddd',
          backgroundColor: 'white',
        }}
      >
        {/* Title & Icon */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationOnOutlinedIcon sx={{ color: 'primary.main' }} />
          <Typography variant='h6' fontWeight='bold'>
            Selected Properties
          </Typography>
        </Box>

        {/* Close Button */}
        <Tooltip title='Close Sidebar'>
          <IconButton onClick={() => setCartOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Property List */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', backgroundColor: '#FAFAFA' }}>
        <List>
          {cartItems.length === 0 ? (
            <Typography sx={{ textAlign: 'center', py: 4, color: 'gray' }}>
              No properties selected.
            </Typography>
          ) : (
            cartItems.map((item) => (
              <React.Fragment key={item.id}>
                <ListItem
                  secondaryAction={
                    <Tooltip title='Remove'>
                      <IconButton
                        edge='end'
                        color='error'
                        onClick={() => togglePolygonSelection(item.id)}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  }
                  sx={{
                    p: 2,
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)' },
                  }}
                >
                  <ListItemText
                    primary={item.properties?.dealName || `Site ${item.id}`}
                    secondary={`${item.properties?.acreage || '?'} acres`}
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))
          )}
        </List>
      </Box>

      {/* Actions Section */}
      <Box sx={{ p: 3, borderTop: '1px solid #ddd', backgroundColor: 'white' }}>
        {/* Remove All Button */}
        <Button
          fullWidth
          variant='outlined'
          color='error'
          startIcon={<ClearAllIcon />}
          onClick={handleRemoveAll}
          disabled={cartItems.length === 0}
          sx={{ mb: 2 }}
        >
          Remove All
        </Button>
        {/* Clear All Button */}
        <Button
          fullWidth
          variant='contained'
          color='primary'
          startIcon={<InsertDriveFileOutlinedIcon />}
          onClick={() => {
            const debug = window.__FACTOR_DEBUG__;

            const markerEntries = Object.entries(debug.markerRadiusMap || {});
            const markers = markerEntries.map(([markerId, radius]) => {
              const circleData = debug.markerToCircleMap?.[markerId];
              return {
                markerId,
                center: circleData?.center,
                radius,
              };
            });

            const drawFeatures =
              debug.drawRef?.current?.getAll()?.features || [];
            const polygons = drawFeatures
              .filter((f) => f.geometry.type === 'Polygon')
              .map((f) => f.geometry.coordinates[0]); // Outer ring only

            debug.setDrawnFeatures?.({ markers, polygons }); // Optional: update the state if needed

            setCartOpen(false);
            setShowReport(true);
          }}
          disabled={cartItems.length === 0}
        >
          Build Comp Report
        </Button>
      </Box>
    </Drawer>
  );
};

export default PropertyCartSidebar;
